/**
 * What the two-step start remembers, per organisation, in this browser:
 * the steps the viewer skipped and whether they finished it. It sits next
 * to the setup reminder's own flag (``rw.onboarding.dismissed.<slug>``,
 * see ``useSetupNudge``) and works the same way: localStorage while it
 * works, the page's memory when storage is refused, and one event so
 * every consumer follows a change at once.
 */
import * as React from "react";

import type { FlowStepKey } from "./onboardingFlow";

export type FlowState = { skipped: FlowStepKey[]; finished: boolean };

const EMPTY: FlowState = { skipped: [], finished: false };
const CHANGE_EVENT = "rw:onboarding-flow-changed";
const storageKey = (slug: string): string => `rw.onboarding.flow.${slug}`;
const shownKey = (slug: string): string => `rw.onboarding.flow-shown.${slug}`;

/** Choices storage refused, kept for the page's life (see useSetupNudge). */
const unsaved = new Map<string, FlowState>();
/** Snapshots by raw value, so useSyncExternalStore gets a stable object. */
const parsed = new Map<string, { raw: string | null; state: FlowState }>();

const STEP_KEYS: readonly FlowStepKey[] = ["agent", "source"];

function parse(raw: string | null): FlowState {
  if (!raw) return EMPTY;
  try {
    const value = JSON.parse(raw) as Partial<FlowState>;
    const skipped = Array.isArray(value.skipped)
      ? STEP_KEYS.filter((k) => value.skipped?.includes(k))
      : [];
    return { skipped, finished: value.finished === true };
  } catch {
    return EMPTY;
  }
}

export function readFlowState(slug: string | null): FlowState {
  if (!slug) return EMPTY;
  const kept = unsaved.get(slug);
  if (kept) return kept;
  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKey(slug));
  } catch {
    return EMPTY;
  }
  const cached = parsed.get(slug);
  if (cached && cached.raw === raw) return cached.state;
  const state = parse(raw);
  parsed.set(slug, { raw, state });
  return state;
}

function write(slug: string, state: FlowState | null): void {
  try {
    if (state) localStorage.setItem(storageKey(slug), JSON.stringify(state));
    else localStorage.removeItem(storageKey(slug));
    unsaved.delete(slug);
  } catch {
    unsaved.set(slug, state ?? EMPTY);
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Remember that the viewer skipped ``step`` in ``slug``'s flow. */
export function skipFlowStep(slug: string, step: FlowStepKey): void {
  const current = readFlowState(slug);
  if (current.skipped.includes(step)) return;
  write(slug, { ...current, skipped: [...current.skipped, step] });
}

/** Remember that the viewer finished the flow. */
export function setFlowFinished(slug: string): void {
  write(slug, { ...readFlowState(slug), finished: true });
}

/** Forget skips and the finish: the flow is offered again. */
export function resetFlowState(slug: string): void {
  write(slug, null);
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** ``slug``'s flow state, kept current. */
export function useFlowState(slug: string | null): FlowState {
  return React.useSyncExternalStore(
    subscribe,
    () => readFlowState(slug),
    () => EMPTY,
  );
}

/** Whether the app already opened the flow by itself in this tab. */
export function autoShownThisSession(slug: string): boolean {
  try {
    return sessionStorage.getItem(shownKey(slug)) === "1";
  } catch {
    // No session storage: never open it by itself rather than every time.
    return true;
  }
}

export function markAutoShown(slug: string): void {
  try {
    sessionStorage.setItem(shownKey(slug), "1");
  } catch {
    // autoShownThisSession already answers true without storage.
  }
}
